// ---------------------------------------------------------------------------
// cities-hub.content.ts — Translated content for CitiesHub component
// ---------------------------------------------------------------------------
import type { SupportedLocale, LocaleContentMap } from '../i18n.config';

export interface CitiesHubCity {
  city: string;
  country: string;
}

export interface CitiesHubContent {
  badge: string;
  title: string;
  subtitle: string;
  regions: {
    americas: string;
    europe: string;
    middleEast: string;
    asiaPacific: string;
    oceania: string;
  };
  map: {
    globalCoverage: string;
    citiesCount: string;
    hotelsServed: string;
  };
  hotelsLabel: string;
  cta: {
    title: string;
    subtitle: string;
  };
  cities: Record<string, CitiesHubCity>;
}

/* -------------------------------------------------------------------------- */
/*  English                                                                   */
/* -------------------------------------------------------------------------- */

const en: CitiesHubContent = {
  badge: '10 Global Cities',
  title: 'Hotel Software by City',
  subtitle:
    'City-specific compliance, popular areas, local tax automation, and property types tailored for the world\'s top hospitality markets.',
  regions: {
    americas: 'Americas',
    europe: 'Europe',
    middleEast: 'Middle East',
    asiaPacific: 'Asia Pacific',
    oceania: 'Oceania',
  },
  map: {
    globalCoverage: 'Global Coverage',
    citiesCount: '10 Cities',
    hotelsServed: '76,000+ Hotels Served',
  },
  hotelsLabel: 'Hotels',
  cta: {
    title: 'Don\'t see your city?',
    subtitle:
      'HotelStack works globally. Our platform adapts to local tax regulations, guest registration requirements, and payment methods in any city worldwide.',
  },
  cities: {
    'hotel-software-dubai': { city: 'Dubai', country: 'UAE' },
    'hotel-software-bangkok': { city: 'Bangkok', country: 'Thailand' },
    'hotel-software-london': { city: 'London', country: 'United Kingdom' },
    'hotel-software-new-york': { city: 'New York', country: 'United States' },
    'hotel-software-singapore': { city: 'Singapore', country: 'Singapore' },
    'hotel-software-paris': { city: 'Paris', country: 'France' },
    'hotel-software-tokyo': { city: 'Tokyo', country: 'Japan' },
    'hotel-software-istanbul': { city: 'Istanbul', country: 'Turkey' },
    'hotel-software-sydney': { city: 'Sydney', country: 'Australia' },
    'hotel-software-los-angeles': { city: 'Los Angeles', country: 'United States' },
  },
};

/* -------------------------------------------------------------------------- */
/*  Arabic                                                                    */
/* -------------------------------------------------------------------------- */

const ar: CitiesHubContent = {
  badge: '١٠ مدن عالمية',
  title: 'برنامج إدارة الفنادق حسب المدينة',
  subtitle:
    'امتثال خاص بكل مدينة، والمناطق الشائعة، وأتمتة الضرائب المحلية، وأنواع العقارات المصممة لأهم أسواق الضيافة في العالم.',
  regions: {
    americas: 'الأمريكتان',
    europe: 'أوروبا',
    middleEast: 'الشرق الأوسط',
    asiaPacific: 'آسيا والمحيط الهادئ',
    oceania: 'أوقيانوسيا',
  },
  map: {
    globalCoverage: 'تغطية عالمية',
    citiesCount: '١٠ مدن',
    hotelsServed: 'أكثر من ٧٦٬٠٠٠ فندق',
  },
  hotelsLabel: 'فندق',
  cta: {
    title: 'لا ترى مدينتك؟',
    subtitle:
      'يعمل HotelStack عالمياً. تتكيف منصتنا مع اللوائح الضريبية المحلية ومتطلبات تسجيل الضيوف وطرق الدفع في أي مدينة حول العالم.',
  },
  cities: {
    'hotel-software-dubai': { city: 'دبي', country: 'الإمارات' },
    'hotel-software-bangkok': { city: 'بانكوك', country: 'تايلاند' },
    'hotel-software-london': { city: 'لندن', country: 'المملكة المتحدة' },
    'hotel-software-new-york': { city: 'نيويورك', country: 'الولايات المتحدة' },
    'hotel-software-singapore': { city: 'سنغافورة', country: 'سنغافورة' },
    'hotel-software-paris': { city: 'باريس', country: 'فرنسا' },
    'hotel-software-tokyo': { city: 'طوكيو', country: 'اليابان' },
    'hotel-software-istanbul': { city: 'إسطنبول', country: 'تركيا' },
    'hotel-software-sydney': { city: 'سيدني', country: 'أستراليا' },
    'hotel-software-los-angeles': { city: 'لوس أنجلوس', country: 'الولايات المتحدة' },
  },
};

/* -------------------------------------------------------------------------- */
/*  Thai                                                                      */
/* -------------------------------------------------------------------------- */

const th: CitiesHubContent = {
  badge: '10 เมืองทั่วโลก',
  title: 'ซอฟต์แวร์โรงแรมตามเมือง',
  subtitle:
    'การปฏิบัติตามกฎระเบียบเฉพาะเมือง พื้นที่ยอดนิยม ระบบภาษีท้องถิ่นอัตโนมัติ และประเภทที่พักที่ปรับแต่งสำหรับตลาดการบริการชั้นนำของโลก',
  regions: {
    americas: 'อเมริกา',
    europe: 'ยุโรป',
    middleEast: 'ตะวันออกกลาง',
    asiaPacific: 'เอเชียแปซิฟิก',
    oceania: 'โอเชียเนีย',
  },
  map: {
    globalCoverage: 'ครอบคลุมทั่วโลก',
    citiesCount: '10 เมือง',
    hotelsServed: 'ให้บริการโรงแรมกว่า 76,000 แห่ง',
  },
  hotelsLabel: 'โรงแรม',
  cta: {
    title: 'ไม่เห็นเมืองของคุณ?',
    subtitle:
      'HotelStack ทำงานได้ทั่วโลก แพลตฟอร์มของเราปรับตัวตามกฎระเบียบภาษีท้องถิ่น ข้อกำหนดการลงทะเบียนผู้เข้าพัก และวิธีการชำระเงินในทุกเมืองทั่วโลก',
  },
  cities: {
    'hotel-software-dubai': { city: 'ดูไบ', country: 'สหรัฐอาหรับเอมิเรตส์' },
    'hotel-software-bangkok': { city: 'กรุงเทพฯ', country: 'ไทย' },
    'hotel-software-london': { city: 'ลอนดอน', country: 'สหราชอาณาจักร' },
    'hotel-software-new-york': { city: 'นิวยอร์ก', country: 'สหรัฐอเมริกา' },
    'hotel-software-singapore': { city: 'สิงคโปร์', country: 'สิงคโปร์' },
    'hotel-software-paris': { city: 'ปารีส', country: 'ฝรั่งเศส' },
    'hotel-software-tokyo': { city: 'โตเกียว', country: 'ญี่ปุ่น' },
    'hotel-software-istanbul': { city: 'อิสตันบูล', country: 'ตุรกี' },
    'hotel-software-sydney': { city: 'ซิดนีย์', country: 'ออสเตรเลีย' },
    'hotel-software-los-angeles': { city: 'ลอสแอนเจลิส', country: 'สหรัฐอเมริกา' },
  },
};

/* -------------------------------------------------------------------------- */
/*  Spanish                                                                   */
/* -------------------------------------------------------------------------- */

const es: CitiesHubContent = {
  badge: '10 Ciudades Globales',
  title: 'Software Hotelero por Ciudad',
  subtitle:
    'Cumplimiento normativo por ciudad, zonas populares, automatizacion fiscal local y tipos de propiedad adaptados a los principales mercados hoteleros del mundo.',
  regions: {
    americas: 'Americas',
    europe: 'Europa',
    middleEast: 'Oriente Medio',
    asiaPacific: 'Asia Pacifico',
    oceania: 'Oceania',
  },
  map: {
    globalCoverage: 'Cobertura Global',
    citiesCount: '10 Ciudades',
    hotelsServed: 'Mas de 76.000 Hoteles',
  },
  hotelsLabel: 'Hoteles',
  cta: {
    title: 'No ves tu ciudad?',
    subtitle:
      'HotelStack funciona a nivel global. Nuestra plataforma se adapta a las regulaciones fiscales locales, requisitos de registro de huespedes y metodos de pago en cualquier ciudad del mundo.',
  },
  cities: {
    'hotel-software-dubai': { city: 'Dubai', country: 'EAU' },
    'hotel-software-bangkok': { city: 'Bangkok', country: 'Tailandia' },
    'hotel-software-london': { city: 'Londres', country: 'Reino Unido' },
    'hotel-software-new-york': { city: 'Nueva York', country: 'Estados Unidos' },
    'hotel-software-singapore': { city: 'Singapur', country: 'Singapur' },
    'hotel-software-paris': { city: 'Paris', country: 'Francia' },
    'hotel-software-tokyo': { city: 'Tokio', country: 'Japon' },
    'hotel-software-istanbul': { city: 'Estambul', country: 'Turquia' },
    'hotel-software-sydney': { city: 'Sidney', country: 'Australia' },
    'hotel-software-los-angeles': { city: 'Los Angeles', country: 'Estados Unidos' },
  },
};

/* -------------------------------------------------------------------------- */
/*  French                                                                    */
/* -------------------------------------------------------------------------- */

const fr: CitiesHubContent = {
  badge: '10 Villes Mondiales',
  title: 'Logiciel Hotelier par Ville',
  subtitle:
    'Conformite locale, quartiers populaires, automatisation fiscale et types de proprietes adaptes aux principaux marches hoteliers du monde.',
  regions: {
    americas: 'Ameriques',
    europe: 'Europe',
    middleEast: 'Moyen-Orient',
    asiaPacific: 'Asie-Pacifique',
    oceania: 'Oceanie',
  },
  map: {
    globalCoverage: 'Couverture Mondiale',
    citiesCount: '10 Villes',
    hotelsServed: 'Plus de 76 000 Hotels',
  },
  hotelsLabel: 'Hotels',
  cta: {
    title: 'Votre ville n\'est pas listee ?',
    subtitle:
      'HotelStack fonctionne a l\'echelle mondiale. Notre plateforme s\'adapte aux reglementations fiscales locales, aux exigences d\'enregistrement des clients et aux modes de paiement dans toute ville du monde.',
  },
  cities: {
    'hotel-software-dubai': { city: 'Dubai', country: 'EAU' },
    'hotel-software-bangkok': { city: 'Bangkok', country: 'Thailande' },
    'hotel-software-london': { city: 'Londres', country: 'Royaume-Uni' },
    'hotel-software-new-york': { city: 'New York', country: 'Etats-Unis' },
    'hotel-software-singapore': { city: 'Singapour', country: 'Singapour' },
    'hotel-software-paris': { city: 'Paris', country: 'France' },
    'hotel-software-tokyo': { city: 'Tokyo', country: 'Japon' },
    'hotel-software-istanbul': { city: 'Istanbul', country: 'Turquie' },
    'hotel-software-sydney': { city: 'Sydney', country: 'Australie' },
    'hotel-software-los-angeles': { city: 'Los Angeles', country: 'Etats-Unis' },
  },
};

/* -------------------------------------------------------------------------- */
/*  German                                                                    */
/* -------------------------------------------------------------------------- */

const de: CitiesHubContent = {
  badge: '10 Weltstadte',
  title: 'Hotelsoftware nach Stadt',
  subtitle:
    'Stadtspezifische Compliance, beliebte Gebiete, lokale Steuerautomatisierung und Immobilientypen fur die fuhrenden Gastgewerbe-Markte der Welt.',
  regions: {
    americas: 'Amerika',
    europe: 'Europa',
    middleEast: 'Naher Osten',
    asiaPacific: 'Asien-Pazifik',
    oceania: 'Ozeanien',
  },
  map: {
    globalCoverage: 'Globale Abdeckung',
    citiesCount: '10 Stadte',
    hotelsServed: 'Uber 76.000 Hotels',
  },
  hotelsLabel: 'Hotels',
  cta: {
    title: 'Ihre Stadt nicht dabei?',
    subtitle:
      'HotelStack funktioniert weltweit. Unsere Plattform passt sich an lokale Steuervorschriften, Gastregistrierungsanforderungen und Zahlungsmethoden in jeder Stadt weltweit an.',
  },
  cities: {
    'hotel-software-dubai': { city: 'Dubai', country: 'VAE' },
    'hotel-software-bangkok': { city: 'Bangkok', country: 'Thailand' },
    'hotel-software-london': { city: 'London', country: 'Vereinigtes Konigreich' },
    'hotel-software-new-york': { city: 'New York', country: 'Vereinigte Staaten' },
    'hotel-software-singapore': { city: 'Singapur', country: 'Singapur' },
    'hotel-software-paris': { city: 'Paris', country: 'Frankreich' },
    'hotel-software-tokyo': { city: 'Tokio', country: 'Japan' },
    'hotel-software-istanbul': { city: 'Istanbul', country: 'Turkei' },
    'hotel-software-sydney': { city: 'Sydney', country: 'Australien' },
    'hotel-software-los-angeles': { city: 'Los Angeles', country: 'Vereinigte Staaten' },
  },
};

/* -------------------------------------------------------------------------- */
/*  Japanese                                                                  */
/* -------------------------------------------------------------------------- */

const ja: CitiesHubContent = {
  badge: '世界10都市',
  title: '都市別ホテルソフトウェア',
  subtitle:
    '都市固有のコンプライアンス、人気エリア、地方税の自動化、世界トップクラスのホスピタリティ市場に合わせた施設タイプ。',
  regions: {
    americas: 'アメリカ大陸',
    europe: 'ヨーロッパ',
    middleEast: '中東',
    asiaPacific: 'アジア太平洋',
    oceania: 'オセアニア',
  },
  map: {
    globalCoverage: 'グローバルカバレッジ',
    citiesCount: '10都市',
    hotelsServed: '76,000以上のホテルに導入',
  },
  hotelsLabel: 'ホテル',
  cta: {
    title: 'お探しの都市が見つかりませんか？',
    subtitle:
      'HotelStackは世界中で利用可能です。当プラットフォームは、世界中のあらゆる都市の現地税制、宿泊者登録要件、決済方法に対応します。',
  },
  cities: {
    'hotel-software-dubai': { city: 'ドバイ', country: 'アラブ首長国連邦' },
    'hotel-software-bangkok': { city: 'バンコク', country: 'タイ' },
    'hotel-software-london': { city: 'ロンドン', country: 'イギリス' },
    'hotel-software-new-york': { city: 'ニューヨーク', country: 'アメリカ合衆国' },
    'hotel-software-singapore': { city: 'シンガポール', country: 'シンガポール' },
    'hotel-software-paris': { city: 'パリ', country: 'フランス' },
    'hotel-software-tokyo': { city: '東京', country: '日本' },
    'hotel-software-istanbul': { city: 'イスタンブール', country: 'トルコ' },
    'hotel-software-sydney': { city: 'シドニー', country: 'オーストラリア' },
    'hotel-software-los-angeles': { city: 'ロサンゼルス', country: 'アメリカ合衆国' },
  },
};

/* -------------------------------------------------------------------------- */
/*  Chinese                                                                   */
/* -------------------------------------------------------------------------- */

const zh: CitiesHubContent = {
  badge: '10座全球城市',
  title: '按城市分类的酒店软件',
  subtitle:
    '针对全球顶级酒店市场量身定制的城市合规性、热门区域、地方税务自动化和物业类型。',
  regions: {
    americas: '美洲',
    europe: '欧洲',
    middleEast: '中东',
    asiaPacific: '亚太地区',
    oceania: '大洋洲',
  },
  map: {
    globalCoverage: '全球覆盖',
    citiesCount: '10座城市',
    hotelsServed: '服务超过76,000家酒店',
  },
  hotelsLabel: '酒店',
  cta: {
    title: '没有找到您的城市？',
    subtitle:
      'HotelStack在全球范围内运营。我们的平台可适应全球任何城市的当地税收法规、住客登记要求和支付方式。',
  },
  cities: {
    'hotel-software-dubai': { city: '迪拜', country: '阿联酋' },
    'hotel-software-bangkok': { city: '曼谷', country: '泰国' },
    'hotel-software-london': { city: '伦敦', country: '英国' },
    'hotel-software-new-york': { city: '纽约', country: '美国' },
    'hotel-software-singapore': { city: '新加坡', country: '新加坡' },
    'hotel-software-paris': { city: '巴黎', country: '法国' },
    'hotel-software-tokyo': { city: '东京', country: '日本' },
    'hotel-software-istanbul': { city: '伊斯坦布尔', country: '土耳其' },
    'hotel-software-sydney': { city: '悉尼', country: '澳大利亚' },
    'hotel-software-los-angeles': { city: '洛杉矶', country: '美国' },
  },
};

/* -------------------------------------------------------------------------- */
/*  Export                                                                    */
/* -------------------------------------------------------------------------- */

const hi: CitiesHubContent = {
  badge: '10 वैश्विक शहर',
  title: 'शहर के अनुसार होटल सॉफ्टवेयर',
  subtitle:
    'शहर-विशिष्ट अनुपालन, लोकप्रिय क्षेत्र, स्थानीय कर स्वचालन, और दुनिया के शीर्ष आतिथ्य बाजारों के लिए अनुकूलित संपत्ति प्रकार।',
  regions: {
    americas: 'अमेरिका',
    europe: 'यूरोप',
    middleEast: 'मध्य पूर्व',
    asiaPacific: 'एशिया प्रशांत',
    oceania: 'ओशिनिया',
  },
  map: {
    globalCoverage: 'वैश्विक कवरेज',
    citiesCount: '10 शहर',
    hotelsServed: '76,000+ होटलों को सेवा',
  },
  hotelsLabel: 'होटल',
  cta: {
    title: 'अपना शहर नहीं दिख रहा?',
    subtitle:
      'HotelStack विश्व स्तर पर काम करता है। हमारा प्लेटफॉर्म दुनिया के किसी भी शहर में स्थानीय कर नियमों, अतिथि पंजीकरण आवश्यकताओं और भुगतान विधियों के अनुसार अनुकूलित होता है।',
  },
  cities: {
    'hotel-software-dubai': { city: 'दुबई', country: 'संयुक्त अरब अमीरात' },
    'hotel-software-bangkok': { city: 'बैंकॉक', country: 'थाईलैंड' },
    'hotel-software-london': { city: 'लंदन', country: 'यूनाइटेड किंगडम' },
    'hotel-software-new-york': { city: 'न्यूयॉर्क', country: 'संयुक्त राज्य अमेरिका' },
    'hotel-software-singapore': { city: 'सिंगापुर', country: 'सिंगापुर' },
    'hotel-software-paris': { city: 'पेरिस', country: 'फ्रांस' },
    'hotel-software-tokyo': { city: 'टोक्यो', country: 'जापान' },
    'hotel-software-istanbul': { city: 'इस्तांबुल', country: 'तुर्की' },
    'hotel-software-sydney': { city: 'सिडनी', country: 'ऑस्ट्रेलिया' },
    'hotel-software-los-angeles': { city: 'लॉस एंजिल्स', country: 'संयुक्त राज्य अमेरिका' },
  },
};

export const CITIES_HUB_CONTENT: LocaleContentMap<CitiesHubContent> = {
  en, ar, th, es, fr, de, ja, zh, hi,
};
